import { Tab, Tabs } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import type { FC, ReactNode } from 'react';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import { Eyebrow, PageBody, PageHeading, PageSurface } from '../../../design-system/components';
import styles from './Learning.module.scss';

interface ResourceEntry {
  title: string;
  meta?: string;
  url: string;
}

interface Publication {
  title: string;
  authors: string;
  journal: string;
  year: string;
  doi: string;
}

const upcomingEvents: ResourceEntry[] = [
  {
    title: 'School of Brain Cells & Circuits "Camillo Golgi" 2026',
    meta: 'Our group co-organizes this winter school',
    url: 'https://www.erice-golgi.org/courses/programme-2026/',
  },
  {
    title: 'EBRAINS Users Days 2026',
    meta: 'Our group will present tutorials, and present our circuit pipelines',
    url: 'https://ebrains.eu/news-and-events/events/2026/ebrains-users-days-2026',
  },
];

const pastEvents: ResourceEntry[] = [
  {
    title: 'EITN EBRAINS Fall School in Computational Neuroscience 2026',
    meta: 'Our group co-organized this fall school',
    url: 'https://ebrains.eu/news-and-events/events/2026/eitn-ebrains-fall-school-in-computational-neuroscience-2026',
  },
  {
    title: '35th Annual Computational Neuroscience Meeting 2026',
    meta: 'Our group presented a poster',
    url: 'https://www.cnsorg.org/cns-2026',
  },
  {
    title: 'Intelligent Robots and Systems 2025',
    meta: 'EBRAINS-Italy booth (B068)',
    url: 'https://www.iros25.org/ExhibitionInfo',
  },
  {
    title: 'Festival della Scienza 2025',
    meta: 'Genova, Italy',
    url: 'https://www.festivalscienza.it/',
  },
  {
    title: 'Society for Neuroscience conference 2025',
    url: 'https://www.sfn.org/meetings/neuroscience-2025/sessions-and-events',
  },
  {
    title: '34th Annual Computational Neuroscience Meeting 2025',
    meta: 'Our group presented tutorials, workshops and posters',
    url: 'https://www.cnsorg.org/cns-2025',
  },
  {
    title: '75th National congress of the Italian Society of Physiology',
    meta: 'Our group gave talks and presented posters',
    url: 'https://www.fisiologiaitaliana.org/congresso_sif.php',
  },
  {
    title: 'Italian Network for Computational and Theoretical Neuroscience Conference 2025',
    meta: 'Our group gave a talk',
    url: 'https://www.incn.it/',
  },
];

const talks: ResourceEntry[] = [
  {
    title: 'Bottom-up and top-down strategies for multiscale brain modelling',
    meta: 'Prof. Egidio D’Angelo — keynote session, 2022',
    url: 'https://youtu.be/5Fthzqoi_cg',
  },
  {
    title: 'Brain models',
    meta: 'Prof. Egidio D’Angelo — TEDxPavia, 2021',
    url: 'https://youtu.be/ttXSJueOtEw',
  },
  {
    title: 'Reconstruction and simulation of the cerebellar microcircuit',
    meta: 'Prof. Claudia Casellato — HBP Student Conference on Interdisciplinary Brain Research, 2020',
    url: 'https://youtu.be/fNEu7oQpGB4?feature=shared',
  },
];

const publications: Publication[] = [
  {
    title: 'Linking cellular-level phenomena to brain architecture: the case of spiking cerebellar controllers.',
    authors: 'D’Angelo E, Antonietti A, Geminiani A, Gambosi B, Alessandro C, Buttarazzi E, Pedrocchi A, Casellato C.',
    journal: 'Neural Networks',
    year: '2025',
    doi: '10.1016/j.neunet.2025.107538',
  },
  {
    title: 'Region-specific mean field models enhance simulations of local and global brain dynamics.',
    authors: 'Lorenzi R, Palesi F, Casellato C, Gandini Wheeler-Kingshott C, D’Angelo E.',
    journal: 'NPJ Syst Biol Appl',
    year: '2025',
    doi: '10.1038/s41540-025-00543-9',
  },
  {
    title: 'Mesoscale simulations predict the role of synergistic cerebellar plasticity during classical eyeblink conditioning.',
    authors: 'Geminiani A, Casellato C, Boele H-J, Pedrocchi, De Zeeuw C I, D’Angelo E.',
    journal: 'PLOS Comp Bio',
    year: '2024',
    doi: '10.1371/journal.pcbi.1011277',
  },
  {
    title: 'A multi-layer mean-field model of the cerebellum embedding microstructure and population-specific dynamics.',
    authors: "Lorenzi R, Geminiani A, Zerlaut Y, De Grazia M, Destexhe A, Gandini C.A.M, Palesi F, Casellato C and D'Angelo E.",
    journal: 'PLOS Comp Bio',
    year: '2023',
    doi: '10.1371/journal.pcbi.1011434',
  },
  {
    title: 'Model simulations unveil the structure-function-dynamics relationship of the cerebellar cortical microcircuit.',
    authors: 'De Schepper R, Geminiani A, Masoli S, Rizza MF, Antonietti A, Casellato C, D’Angelo E.',
    journal: 'Nat Comms Biol',
    year: '2022',
    doi: '10.1038/s42003-022-04213-y',
  },
  {
    title: 'Response Dynamics in an Olivocerebellar Spiking Neural Network With Non-linear Neuron Properties.',
    authors: 'Geminiani A, Pedrocchi A, D’Angelo E, Casellato C.',
    journal: 'Front. Comput. Neurosci.',
    year: '2019',
    doi: '10.3389/fncom.2019.00068',
  },
  {
    title: 'Complex Electroresponsive Dynamics in Olivocerebellar Neurons Represented With Extended-Generalized Leaky Integrate and Fire Models.',
    authors: 'Geminiani A, Casellato C, Pedrocchi A, D’Angelo E.',
    journal: 'Front. Comput. Neurosci.',
    year: '2019',
    doi: '10.3389/fncom.2019.00035',
  },
  {
    title: 'Reconstruction and Simulation of a Scaffold Model of the Cerebellar Network.',
    authors: 'Casali S, Marenzi E, Medini C, Casellato C, D’Angelo E.',
    journal: 'Front. Neuroinform.',
    year: '2019',
    doi: '10.3389/fninf.2019.00037',
  },
  {
    title: 'Complex dynamics in simplified neuronal models: reproducing Golgi cell electroresponsiveness.',
    authors: 'Geminiani A, Casellato C, Locatelli F, Prestori F, Pedrocchi A, D’Angelo E.',
    journal: 'Front. Neuroinform.',
    year: '2018',
    doi: '10.3389/fninf.2018.00088',
  },
  {
    title: 'Modelling the cerebellar microcircuit: new strategies for a long-standing issue.',
    authors: 'D’Angelo E, Antonietti A, Casali S, Casellato C, et al.',
    journal: 'Front. Cell. Neurosci.',
    year: '2016',
    doi: '10.3389/fncel.2016.00176',
  },
  {
    title: 'Distributed circuit plasticity: new clues for the cerebellar mechanisms of learning.',
    authors: 'D’Angelo E, Mapelli L, Casellato C, Garrido J.A, Luque N, Monaco J, Prestori F, Pedrocchi A, Ros E.',
    journal: 'The Cerebellum',
    year: '2016',
    doi: '10.1007/s12311-015-0711-7',
  },
];

const Section: FC<{ eyebrow: string; title: string; children: ReactNode }> = ({ eyebrow, title, children }) => (
  <section className={styles.section}>
    <header className={styles.section__header}>
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className={styles.section__title}>{title}</h2>
    </header>
    {children}
  </section>
);

// A ruled index row: mono kind label, title, meta, and an outbound marker.
const ResourceRow: FC<{ kind: string; title: ReactNode; meta?: ReactNode; url: string }> = ({
  kind,
  title,
  meta,
  url,
}) => (
  <li>
    <a className={styles.row} href={url} target="_blank" rel="noopener noreferrer">
      <span className={styles.row__kind}>{kind}</span>
      <span className={styles.row__title}>{title}</span>
      <span className={styles.row__meta}>{meta}</span>
      <span className={styles.row__marker} aria-hidden="true">
        ↗
      </span>
    </a>
  </li>
);

const Learning: FC = () => {
  const navigate = useNavigate();
  const [eventsTab, setEventsTab] = useState(0);

  const handleNavItemClick = (item: string) => {
    switch (item) {
      case 'Model Exploration':
        navigate('/model-exploration');
        break;
      case 'Tools':
        navigate('/tools');
        break;
      case 'Learning':
        navigate('/learning');
        break;
      case 'Contact':
        navigate('/contact');
        break;
      default:
        break;
    }
  };

  const events = eventsTab === 0 ? upcomingEvents : pastEvents;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header pageTitle="Learning" onNavItemClick={handleNavItemClick} />

      <PageSurface>
        <PageHeading
          title="Events, talks and publications"
          lede="Where the group is presenting, and the recorded material and papers behind the models on this site."
        />

        <PageBody>
          <Section eyebrow="Events" title="Where we are presenting">
            <Tabs
              value={eventsTab}
              onChange={(_, value: number) => setEventsTab(value)}
              className={styles.tabs}
            >
              <Tab label="Upcoming" />
              <Tab label="Past" />
            </Tabs>
            <ul className={styles.rows}>
              {events.map((event) => (
                <ResourceRow
                  key={event.url}
                  kind="Event"
                  title={event.title}
                  meta={event.meta}
                  url={event.url}
                />
              ))}
            </ul>
          </Section>

          <Section eyebrow="Educational content" title="Recorded talks">
            <ul className={styles.rows}>
              {talks.map((talk) => (
                <ResourceRow key={talk.url} kind="Talk" title={talk.title} meta={talk.meta} url={talk.url} />
              ))}
            </ul>
          </Section>

          <Section eyebrow="Main publications" title="Papers behind the models">
            <ul className={styles.rows}>
              {publications.map((publication) => (
                <ResourceRow
                  key={publication.doi}
                  kind={publication.year}
                  title={
                    <>
                      {publication.title}
                      <span className={styles.row__authors}>{publication.authors}</span>
                    </>
                  }
                  meta={
                    <>
                      <span className={styles.row__journal}>{publication.journal}</span>
                      <span className={styles.row__doi}>doi:{publication.doi}</span>
                    </>
                  }
                  url={`https://doi.org/${publication.doi}`}
                />
              ))}
            </ul>
          </Section>
        </PageBody>
      </PageSurface>

      <Footer />
    </div>
  );
};

export default Learning;
